import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiBase, fetchUsingApi } from "../core/apiClient";

export interface EntityConfiguration {
  id: number;
  type: EntityConfigurationType;
  entityType: EntityConfigurationEntityType;
  entityId: number;
  value: string;
}
export enum EntityConfigurationEntityType {
  BUILDING = "BUILDING",
  HOUSEHOLDER = "HOUSEHOLDER",
  DEVELOPMENT = "DEVELOPMENT",
}
export enum EntityConfigurationType {
  QUESTIONS_PROMPT = "QUESTIONS_PROMPT",
  WARRANTY_PROMPT = "WARRANTY_PROMPT",
  PROPERTY_MANAGER_CASE_PROMPT = "PROPERTY_MANAGER_CASE_PROMPT",
  TASK_DEFINITION_PROMPT = "TASK_DEFINITION_PROMPT",
  WELCOME_EMAIL = "WELCOME_EMAIL",
  HAUSING_CLIENT_ID = "HAUSING_CLIENT_ID",
  HAUSING_CLIENT_SECRET = "HAUSING_CLIENT_SECRET",
  HAUSING_COMPANY_ID = "HAUSING_COMPANY_ID",
  HAUSING_BUILDING_ID = "HAUSING_BUILDING_ID",
  HAUSING_TICKET_TITLE = "HAUSING_TICKET_TITLE",
  HAUSING_TICKET_DESCRIPTION = "HAUSING_TICKET_DESCRIPTION",
  HAUSING_SYNC_DISABLED = "HAUSING_SYNC_DISABLED",
  HAUSING_MESSAGE_SYNC_DISABLED = "HAUSING_MESSAGE_SYNC_DISABLED",
  EXPOSE_SUBMITTER_EMAIL_IN_HAUSING = "EXPOSE_SUBMITTER_EMAIL_IN_HAUSING",
  WARRANTY_EMAIL_USER_LIST = "WARRANTY_EMAIL_USER_LIST",
  FRONTEND_FEATURE_FLAGS = "FRONTEND_FEATURE_FLAGS",
  WARRANTY_SYNC_EMAIL = "WARRANTY_SYNC_EMAIL",
  WARRANTY_AUTO_REPLY_MESSAGE = "WARRANTY_AUTO_REPLY_MESSAGE",
  HOUSEHOLD_IMAGES_PATTERN = "HOUSEHOLD_IMAGES_PATTERN",
  CUSTOM_WARRANTY_NOTIFICATION_EMAIL_TEMPLATE = "CUSTOM_WARRANTY_NOTIFICATION_EMAIL_TEMPLATE",
  ESCALATION_QUESTION_EMAIL_FORWARDING_RULE = "ESCALATION_QUESTION_EMAIL_FORWARDING_RULE",
  ENABLE_HAUSING_FOR_BUILDING_IN_DEV = "ENABLE_HAUSING_FOR_BUILDING_IN_DEV",
  TALLY_FORM = "TALLY_FORM",
  LOOKER_REPORT_LINK = "LOOKER_REPORT_LINK",
  INTERNAL_ANALYTICS_ENABLED = "INTERNAL_ANALYTICS_ENABLED",
  FILE_UPLOAD_MANDATORY = "FILE_UPLOAD_MANDATORY",
  CASE_REOPEN_ON_REPLY_ENABLED = "CASE_REOPEN_ON_REPLY_ENABLED",
  CHAT_PRESET_QUESTIONS = "CHAT_PRESET_QUESTIONS",
  STRUCTURED_WARRANTY_RULES = "STRUCTURED_WARRANTY_RULES",
  VOICE_WARRANTY_PROMPT = "VOICE_WARRANTY_PROMPT",
  WARRANTY_DISABLED_MESSAGE = "WARRANTY_DISABLED_MESSAGE",
  HAUSING_OPT_OUT = "HAUSING_OPT_OUT",

  // HOUSEHOLD
  HAUSING_ROOM_ID = "HAUSING_ROOM_ID",
  ONBOARDING_TALLY_FORM_COMPLETED = "ONBOARDING_TALLY_FORM_COMPLETED",
  SHOW_CALL_LINKS = "SHOW_CALL_LINKS",
  MANAGER_HOUSEHOLD = "MANAGER_HOUSEHOLD",
}

export interface ConfigurationUpdateRequest {
  entityType: EntityConfigurationEntityType;
  buildingId: number;
  configurationType: EntityConfigurationType;
  configurationValue: string;
}

export interface EscalationStatus {
  enabled: boolean;
}

const getBuildingConfiguration = async (buildingId: number) => {
  const response = await fetchUsingApi<EntityConfiguration[]>(
    `${apiBase}/configuration/v1/list?buildingId=${buildingId}`,
    "GET",
    (e) => e as EntityConfiguration[],
  );

  if (!response.success) {
    throw new Error(response.message || "Failed to fetch configuration");
  }

  return response.result;
};

const getHouseholdEscalationStatus = async (householdId: number) => {
  const response = await fetchUsingApi<EscalationStatus>(
    `${apiBase}/configuration/v1/household/${householdId}/escalation-status`,
    "GET",
    (e) => e as EscalationStatus,
  );

  if (!response.success) {
    throw new Error(response.message || "Failed to fetch escalation status");
  }

  return response.result;
};

const updateConfiguration = async (request: ConfigurationUpdateRequest) => {
  const response = await fetchUsingApi<boolean>(
    `${apiBase}/configuration/v1/update`,
    "POST",
    (e) => e as boolean,
    JSON.stringify(request),
  );

  if (!response.success) {
    throw new Error(response.message || "Failed to update configuration");
  }

  return response.result;
};

export const resetTallyFormStatusesForBuilding = (buildingId: number) => {
  return fetchUsingApi<boolean>(
    `${apiBase}/configuration/v1/building/${buildingId}/reset-tally-form-status`,
    "POST",
    (e) => e as boolean,
  );
};

export const useBuildingConfiguration = (buildingId: number) => {
  return useQuery({
    queryKey: ["building-configuration", buildingId],
    queryFn: () => getBuildingConfiguration(buildingId),
    enabled: buildingId > 0, // Only fetch if we have a valid building ID
  });
};

export const useHouseholdEscalationStatus = (householdId: number) => {
  return useQuery({
    queryKey: ["household-escalation-status", householdId],
    queryFn: () => getHouseholdEscalationStatus(householdId),
    enabled: householdId > 0,
  });
};

export const useUpdateConfiguration = () => {
  return useMutation({
    mutationFn: updateConfiguration,
    // Optional: You can add onSuccess/onError handlers
  });
};

export const useResetTallyFormStatusesForBuilding = () => {
  return useMutation({
    mutationFn: resetTallyFormStatusesForBuilding,
  });
};

// Household configurations

export interface HouseholdWithConfigurations {
  householdId: number;
  title: string;
  configurations: Record<string, string>;
}

const getHouseholdConfigurations = async (buildingId: number) => {
  const response = await fetchUsingApi<HouseholdWithConfigurations[]>(
    `${apiBase}/configuration/v1/building/${buildingId}/household-configurations`,
    "GET",
    (e) => e as HouseholdWithConfigurations[],
  );

  if (!response.success) {
    throw new Error(response.message || "Failed to fetch household configurations");
  }

  return response.result;
};

export const useHouseholdConfigurations = (buildingId: number) => {
  return useQuery({
    queryKey: ["household-configurations", buildingId],
    queryFn: () => getHouseholdConfigurations(buildingId),
    enabled: buildingId > 0,
  });
};

export interface HouseholdConfigurationUpdateRequest {
  configurationType: string;
  configurationValue: string;
}

const updateHouseholdConfiguration = async ({
  householdId,
  request,
}: {
  householdId: number;
  request: HouseholdConfigurationUpdateRequest;
}) => {
  const response = await fetchUsingApi<boolean>(
    `${apiBase}/configuration/v1/household/${householdId}/configuration`,
    "POST",
    (e) => e as boolean,
    JSON.stringify(request),
  );

  if (!response.success) {
    throw new Error(response.message || "Failed to update household configuration");
  }

  return response.result;
};

export const useUpdateHouseholdConfiguration = (buildingId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateHouseholdConfiguration,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["household-configurations", buildingId] });
    },
  });
};
